// BYO-01: the OAuth providers (Google, GitHub, Slack; see @companyos/domain-agents/connect). Each needs the
// founder's OAuth app on the server; until then the card says "Needs admin setup" and names the
// environment variables, never their values. Once set up, "Connect" asks the API for the vendor's
// consent URL (PKCE, sealed state) and the browser goes there; /connect/callback finishes.
import { newIdempotencyKey } from "@companyos/sdk";
import { Badge, Button, Checkbox, HomeCardRow, Label, Notice, toast } from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { COPY } from "./copy";
import type { Provider } from "./queries";

/** Where the callback page finds the company it came from (same tab, same origin only). */
export const CONNECT_RETURN_KEY = "cos.connect.return";

/** One service with its access tiers and Connect (also the Catalog's Connect modal, INC2-CONTACT). */
export function ProviderRow({
  api,
  co,
  slug,
  scope,
  p,
  disabled,
}: {
  api: Api;
  co: string;
  slug: string;
  scope: "person" | "company";
  p: Provider;
  disabled: boolean;
}) {
  const base = useId();
  const offered = p.tiers.filter((t) => !t.founder_gated);
  const [tiers, setTiers] = useState<string[]>(offered[0] ? [offered[0].key] : []);
  const start = useMutation({
    mutationFn: () =>
      api.client.connections.oauthStart({
        params: { co, provider: p.key as Exclude<Provider["key"], "api_key"> },
        query: { scope, tiers: tiers.join(",") },
      }),
    onSuccess: (r) => {
      try {
        sessionStorage.setItem(CONNECT_RETURN_KEY, JSON.stringify({ co, slug }));
      } catch {
        // No storage: the callback page then asks the person to go back by hand.
      }
      window.location.assign(r.authorize_url);
    },
    onError: (e) => toast.add({ title: problemText(e, "Could not start."), type: "error" }),
  });
  return (
    <HomeCardRow
      data-provider={p.key}
      title={<span className="font-medium">{p.name}</span>}
      description={
        <span className="flex flex-col gap-2">
          <span>{p.summary}</span>
          {p.configured ? (
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              {offered.map((t) => (
                <span key={t.key} className="flex items-center gap-2">
                  <Checkbox
                    id={`${base}-${t.key}`}
                    checked={tiers.includes(t.key)}
                    onCheckedChange={(v) =>
                      setTiers((x) => (v === true ? [...x, t.key] : x.filter((k) => k !== t.key)))
                    }
                  />
                  <Label htmlFor={`${base}-${t.key}`} className="font-normal" title={t.gives}>
                    {t.label}
                  </Label>
                </span>
              ))}
            </span>
          ) : (
            <span className="text-xs">
              {COPY.oauthNeedsSetup} <span className="font-mono">{p.setup_env.join(", ")}</span>
            </span>
          )}
        </span>
      }
      trailing={
        p.configured ? (
          <Button
            size="sm"
            disabled={disabled || tiers.length === 0 || start.isPending}
            onClick={() => start.mutate()}
          >
            Connect
          </Button>
        ) : (
          <Badge variant="warning">Needs admin setup</Badge>
        )
      }
    />
  );
}

/** The `/connect/callback` page: posts the vendor's `code` and sealed `state` to the API. */
export function ConnectCallback({
  api,
  search,
  onDone,
}: {
  api: Api;
  search: { code?: string; state?: string; error?: string };
  onDone: (slug: string | null) => void;
}) {
  let back: { co: string; slug: string } | null = null;
  try {
    back = JSON.parse(sessionStorage.getItem(CONNECT_RETURN_KEY) ?? "null");
  } catch {
    back = null;
  }
  const done = useMutation({
    mutationFn: (x: { co: string; code: string; state: string }) =>
      api.client.connections.oauthComplete({
        params: { co: x.co },
        idempotencyKey: newIdempotencyKey(),
        body: { code: x.code, state: x.state },
      }),
    onSuccess: (c) => {
      try {
        sessionStorage.removeItem(CONNECT_RETURN_KEY);
      } catch {
        // ignore
      }
      toast.add({ title: `${c.label} connected`, type: "success" });
      onDone(back?.slug ?? null);
    },
  });
  // Once: the vendor's code is single-use.
  const sent = useRef(false);
  const { mutate } = done;
  const co = back?.co;
  useEffect(() => {
    if (sent.current || !co || !search.code || !search.state || search.error) return;
    sent.current = true;
    mutate({ co, code: search.code, state: search.state });
  }, [co, search.code, search.state, search.error, mutate]);
  const problem = search.error
    ? `The service said: ${search.error}. Nothing was connected.`
    : !back || !search.code || !search.state
      ? "This page finishes a connection started from My subscriptions; nothing is waiting here."
      : done.isError
        ? problemText(done.error, "The connection was not finished.")
        : null;
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4 p-6">
      {problem ? (
        <Notice tone="danger" title="Not connected">
          {problem}
        </Notice>
      ) : (
        <Loading label="Finishing the connection" rows={2} />
      )}
      <div>
        <Button variant="secondary" onClick={() => onDone(back?.slug ?? null)}>
          Back
        </Button>
      </div>
    </div>
  );
}
